import { useEffect, useState } from 'react'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { Link, List, Button, Tooltip, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { useReselector } from '@/utils/sharedHooks'

import { getCurrentUser } from '@/selectors/user.selectors'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  destroySingleUserDocument,
  UserDocument,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'

// TODO: Migrate to V2
import FileUploadButton from '@/componentsV1/FileUpload/FileUploadButton'
import { UploadedFile } from '@/componentsV1/FileUpload/FileUploadModal'

interface TransactionReceiptsProps {
  transaction: TransactionOrSplit
  buttonText?: string
  setUploadedFileIds?: (fileId: number) => void
  setDeletedFileIds?: (fileId: number) => void
}

export const DisplayFile = ({
  receipt,
  deleteFile,
}: {
  receipt: UserDocument | UploadedFile
  deleteFile: (id: number) => void
}) => {
  const documents = useReselector(selectDocuments)
  const doc = documents[receipt.id]
  if (!doc) {
    return null
  }

  return (
    <List.Item key={receipt.id}>
      <div className="flex flex-row items-center">
        <Link href={doc?.signedUrl || ''} color={TextColors.EMPHASIS} newPage>
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Button variant="link">{doc?.fileDescription}</Button>
            </Tooltip.Trigger>

            <Tooltip.Content>
              <Text
                variant={TypographyStyles.BODY_SM}
              >{`File Name: ${doc?.id} ${doc?.fileDescription}`}</Text>
            </Tooltip.Content>
          </Tooltip>
        </Link>
        <Button
          variant="discreet"
          size="square"
          iconLeft={faTrashCan}
          onClick={() => deleteFile(receipt.id)}
        />
      </div>
    </List.Item>
  )
}

export const TransactionReceiptsPanel = ({
  transaction,
  setUploadedFileIds,
  setDeletedFileIds,
}: TransactionReceiptsProps) => {
  const currentUser = useReselector(getCurrentUser) || undefined
  const [filesToDisplay, setFilesToDisplay] = useState<
    (UserDocument | UploadedFile)[] | undefined
  >(transaction?.receipts)
  const [uploadedFile, setUploadedFile] = useState<UploadedFile>()
  const dispatch = useAppDispatch()
  const deleteFile = async (fileId: number) => {
    await destroySingleUserDocument(fileId)(dispatch)
    setDeletedFileIds?.(fileId)
  }

  useEffect(() => {
    if (!transaction?.id) {
      if (uploadedFile) {
        setFilesToDisplay((currentFiles) =>
          currentFiles ? [...currentFiles, uploadedFile] : [uploadedFile]
        )
        setUploadedFileIds?.(uploadedFile.id)
      }
    } else {
      setFilesToDisplay(transaction?.receipts)
      if (uploadedFile) {
        setUploadedFileIds?.(uploadedFile.id)
      }
    }
  }, [transaction?.receipts, transaction?.id, uploadedFile, setUploadedFileIds])

  return (
    <>
      <FileUploadButton
        buttonText="Upload Receipt"
        userFacing
        user={currentUser}
        documentType={UploadDocumentType.RECEIPT}
        setUploadedFile={setUploadedFile}
        extraPayload={{ transactionId: Number(transaction?.id) }}
      />
      <List>
        {filesToDisplay?.map((receipt) => (
          <DisplayFile
            key={receipt.id}
            receipt={receipt}
            deleteFile={deleteFile}
          />
        ))}
      </List>
    </>
  )
}
